/**
 * Which Supabase service a request targets (JOSH-89).
 *
 * The URL is parsed with `new URL`; the origin must equal the project's origin
 * exactly and the service prefix is matched on whole path segments, so
 * `https://ref.supabase.co.evil.tld` or `/rest/v10` never match.
 */

export type RouteKind = 'auth' | 'rest' | 'storage' | 'functions' | 'other';

export interface Route {
  kind: RouteKind;
  /** True for `POST /auth/v1/token?grant_type=refresh_token`. */
  refreshGrant: boolean;
}

const SERVICES: ReadonlySet<string> = new Set(['auth', 'rest', 'storage', 'functions']);
const OTHER: Route = { kind: 'other', refreshGrant: false };

/** The request URL of a `fetch` input, or null when it cannot be parsed. */
export function requestUrl(input: RequestInfo | URL): URL | null {
  try {
    if (input instanceof URL) return input;
    return new URL(typeof input === 'string' ? input : input.url);
  } catch {
    return null;
  }
}

function segments(pathname: string): string[] {
  return pathname.split('/').filter(Boolean);
}

/**
 * Builds a classifier bound to one project URL. An empty or invalid
 * `supabaseUrl` classifies everything as `other` (pure passthrough).
 */
export function createRouteClassifier(supabaseUrl: string): (input: RequestInfo | URL) => Route {
  let base: URL | null = null;
  try {
    base = supabaseUrl ? new URL(supabaseUrl) : null;
  } catch {
    base = null;
  }
  if (!base) return () => OTHER;
  const origin = base.origin;
  const baseSegments = segments(base.pathname);

  return (input) => {
    const url = requestUrl(input);
    if (!url || url.origin !== origin) return OTHER;
    const parts = segments(url.pathname);
    if (baseSegments.some((segment, i) => parts[i] !== segment)) return OTHER;
    const [service, version, ...rest] = parts.slice(baseSegments.length);
    if (version !== 'v1' || !SERVICES.has(service)) return OTHER;
    const kind = service as Exclude<RouteKind, 'other'>;
    const refreshGrant =
      kind === 'auth' &&
      rest.length === 1 &&
      rest[0] === 'token' &&
      url.searchParams.get('grant_type') === 'refresh_token';
    return { kind, refreshGrant };
  };
}
